/** Privacy Policy section content — UK TO MARS LTD */

export const privacyIntro = [
  "UK TO MARS LTD is committed to protecting your personal data and handling it responsibly, lawfully, and transparently.",
  "This Privacy Policy explains how we collect, use, store, and share personal data when you visit our website, create an account, request a quotation, place an order, use our shipment tracking services, contact us, or use our wholesale, export, and logistics services.",
  "We process personal data in accordance with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, the Privacy and Electronic Communications Regulations (PECR), and relevant UK tax, customs, and HMRC record-keeping requirements.",
];

export const cookieTableRows = [
  {
    type: "Strictly necessary",
    purpose:
      "Enable core website functions such as page navigation, account login, quote basket and website security.",
    consent: "No — exempt under PECR",
  },
  {
    type: "Functional",
    purpose:
      "Remember preferences such as language, region or saved settings to improve your experience.",
    consent: "Yes",
  },
  {
    type: "Performance / analytics",
    purpose:
      "Help us understand how visitors use the website so we can improve it.",
    consent: "Yes",
  },
  {
    type: "Marketing / targeting",
    purpose:
      "Track browsing activity to deliver relevant advertising or measure campaign performance, where used.",
    consent: "Yes",
  },
];

export const privacySections = [
  {
    title: "Who We Are",
    whoWeAre: true,
    closing: [
      "For the purpose of UK data protection law, UK TO MARS LTD is the data controller of the personal data we collect and process. For any data protection queries, please contact us using the details above.",
    ],
  },
  {
    title: "Information We Collect",
    paragraphs: ["We may collect the following information:"],
    subsections: [
      {
        heading: "Business and contact details",
        bullets: [
          "name;",
          "company name;",
          "job title;",
          "email address;",
          "telephone number;",
          "WhatsApp number;",
          "billing address;",
          "delivery address;",
          "business registration details;",
          "VAT number;",
          "EORI number.",
        ],
      },
      {
        heading: "Account information",
        bullets: [
          "username;",
          "password;",
          "account login details;",
          "account preferences;",
          "quote basket activity;",
          "order history.",
        ],
      },
      {
        heading: "Order, quotation and transaction information",
        bullets: [
          "product enquiries;",
          "quotation requests;",
          "invoices;",
          "payment confirmation;",
          "delivery and shipment details;",
          "returns, claims and customer support records.",
        ],
      },
      {
        heading: "Export, customs and compliance information",
        bullets: [
          "consignee details;",
          "destination country;",
          "end-user or importer details;",
          "customs documents;",
          "shipping documents;",
          "proof of export;",
          "import/export licence information where required;",
          "identity confirmation or proof of address where required for compliance.",
        ],
      },
      {
        heading: "Website and technical information",
        bullets: [
          "IP address;",
          "browser type;",
          "device type;",
          "operating system;",
          "pages visited;",
          "search activity;",
          "cookies;",
          "website usage and analytics data.",
        ],
      },
      {
        heading: "Communication records",
        bullets: [
          "emails;",
          "website forms;",
          "WhatsApp messages;",
          "telephone notes;",
          "customer service records.",
        ],
      },
    ],
  },
  {
    title: "How We Collect Information",
    paragraphs: ["We collect information when you:"],
    bullets: [
      "visit our website;",
      "create an account;",
      "submit a quotation or product enquiry;",
      "add products to your quote basket;",
      "place an order;",
      "contact us by email, phone, WhatsApp or form;",
      "use shipment tracking;",
      "provide export, customs or delivery information;",
      "interact with our marketing or promotional communications.",
    ],
    closing: [
      "We may also receive information from third parties such as freight forwarders, customs agents, payment providers, logistics partners, credit reference providers, suppliers, IT providers, and public business registers.",
    ],
  },
  {
    title: "How We Use Your Information",
    paragraphs: ["We use your personal data to:"],
    bullets: [
      "create and manage customer accounts;",
      "process enquiries and quotations;",
      "confirm product availability and pricing;",
      "process orders and invoices;",
      "arrange delivery, collection and export logistics;",
      "prepare export, customs, VAT and shipping documentation;",
      "provide shipment tracking where available;",
      "communicate with you about orders and services;",
      "manage customer support, claims and disputes;",
      "verify identity, business status and compliance requirements;",
      "prevent fraud, unlawful activity and misuse of our website;",
      "comply with UK tax, customs, VAT, accounting and legal obligations;",
      "improve our website, services and customer experience;",
      "send relevant business updates, offers or marketing where lawful.",
    ],
  },
  {
    title: "Lawful Basis for Processing",
    paragraphs: ["We rely on the following lawful bases:"],
    bullets: [
      "Contract — to provide quotations, process orders, supply goods, arrange shipment, and provide customer support.",
      "Legal obligation — to comply with VAT, HMRC, customs, accounting, export control, sanctions, fraud prevention, and other legal requirements.",
      "Legitimate interests — to operate and improve our business, protect our website, manage customer relationships, prevent fraud, assess business risk, recover debts, and communicate with business customers about relevant services.",
      "Consent — where required for optional marketing, cookies, or specific processing that requires your clear permission.",
    ],
  },
  {
    title: "Marketing Communications",
    paragraphs: [
      "We may send business customers relevant updates about products, promotions, stock availability, and services.",
      "You can opt out of marketing communications at any time by contacting us or using any unsubscribe option provided.",
      "We may still send non-marketing messages relating to orders, accounts, payments, compliance, delivery, or service updates.",
    ],
  },
  {
    title: "Cookies and Website Analytics",
    cookieSection: true,
  },
  {
    title: "Who We Share Data With",
    paragraphs: ["We may share personal data where necessary with:"],
    bullets: [
      "freight forwarders;",
      "shipping carriers;",
      "logistics providers;",
      "customs brokers;",
      "customs authorities;",
      "chambers of commerce;",
      "official veterinarians or inspection bodies where required;",
      "suppliers;",
      "payment providers;",
      "banks;",
      "accountants;",
      "auditors;",
      "insurers;",
      "legal advisers;",
      "IT, website and cloud service providers;",
      "fraud prevention and credit reference providers;",
      "HMRC and other government or regulatory authorities.",
    ],
    closing: ["We do not sell personal data."],
  },
  {
    title: "International Transfers",
    paragraphs: [
      "As an export business, we may transfer limited personal data outside the UK where necessary for international shipping, customs clearance, documentation, delivery, tracking, or customer communication.",
      "Where required, we use appropriate safeguards under UK GDPR, such as contracts, adequacy arrangements, or other lawful transfer mechanisms.",
    ],
  },
  {
    title: "Shipment Tracking",
    paragraphs: [
      "Where shipment tracking is available, we may process shipment reference numbers, tracking numbers, delivery updates, GPS or route-related data, and carrier information.",
      "Tracking availability depends on the shipment, destination country, route, carrier, and logistics provider. Tracking may not be available for all destinations.",
    ],
  },
  {
    title: "Credit, Fraud and Compliance Checks",
    paragraphs: [
      "Where necessary, we may carry out checks to verify customer identity, business status, creditworthiness, fraud risk, sanctions risk, or compliance requirements.",
      "This may involve checking information against public records, business registers, credit reference agencies, fraud prevention databases, sanctions lists, or other lawful sources.",
      "If you do not provide required information, we may be unable to open an account, provide credit, process an order, arrange export, or supply goods.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We keep personal data only for as long as necessary.",
      "Typical retention periods include:",
    ],
    bullets: [
      "customer account and order records: for the duration of the business relationship and a reasonable period after;",
      "VAT, customs, accounting and tax records: generally at least 6 years;",
      "export documentation and proof of export: retained as required for tax, customs and audit purposes;",
      "marketing records: until you unsubscribe or object;",
      "website analytics: for a limited period depending on the tool used.",
    ],
    closing: [
      "We may keep records longer where required for legal claims, investigations, audits, regulatory requirements, or dispute resolution.",
    ],
  },
  {
    title: "Data Security",
    paragraphs: [
      "We use appropriate technical and organisational measures to protect personal data, including:",
    ],
    bullets: [
      "password protection;",
      "restricted access controls;",
      "secure cloud storage;",
      "website security measures;",
      "staff access limitations;",
      "backup and recovery procedures;",
      "data handling controls.",
    ],
    closing: [
      "No system can be guaranteed completely secure, but we take reasonable steps to protect personal data from unauthorised access, loss, misuse, or disclosure.",
    ],
  },
  {
    title: "Your Rights",
    paragraphs: ["Under UK GDPR, you may have the right to:"],
    bullets: [
      "access your personal data;",
      "correct inaccurate data;",
      "request deletion of your data;",
      "restrict processing;",
      "object to processing;",
      "request data portability;",
      "withdraw consent where consent is used;",
      "complain to the Information Commissioner's Office.",
    ],
    closing: [
      "Some rights may be limited where we must keep data for legal, tax, customs, accounting, or contractual reasons.",
    ],
    rightsContact: true,
  },
  {
    title: "Children's Data",
    paragraphs: [
      "Our website and services are intended for business users and are not directed at children.",
      "We do not knowingly collect personal data from children under 18.",
    ],
  },
  {
    title: "Third-Party Websites",
    paragraphs: [
      "Our website may contain links to third-party websites, platforms, suppliers, carriers, or service providers.",
      "We are not responsible for the privacy practices, security, or content of third-party websites. Please review their privacy policies before providing personal data.",
    ],
  },
  {
    title: "CCTV and Site Security",
    paragraphs: [
      "If you visit our premises, CCTV or security systems may operate for safety, security, crime prevention, and operational purposes.",
      "CCTV footage may be shared with law enforcement, insurers, or legal advisers where necessary and lawful.",
    ],
  },
  {
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time to reflect changes in our business, website, services, legal requirements, or regulatory guidance.",
      "The latest version will be published on our website with the updated date.",
    ],
  },
  {
    title: "Contact Us",
    paragraphs: [
      "For questions about this Privacy Policy or how we handle personal data, please contact:",
    ],
    contact: true,
  },
];
